import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { AppProfile } from '../../../shared/app-profile/app-profile';

export interface Organization {
  orgId: string;
  name: string;
  displayName: string;
  address: {
    address1: string;
    address2: string;
    country: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
    pointOfContactDetails?: string;
  };
  organizationTypeId: string;
  soldTo?: string;
  shipTo?: string;
  clientCertificateVerificationRequired: boolean;
  excludeReferrals: boolean;
  locale?: string;
  logoObjectKey?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UpdateOrganizationRequest {
  orgId: string;
  name: string;
  displayName: string;
  address: {
    address1: string;
    address2: string;
    country: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
    pointOfContactDetails?: string;
  };
  organizationTypeId: string;
  shipTo?: string;
  soldTo?: string;
  clientCertificateVerificationRequired: boolean;
  excludeReferrals: boolean;
  locale?: string;
  logoObjectKey?: string;
}

@Injectable({
  providedIn: 'root'
})
export class EditOrganizationService {
  constructor(
    private readonly http: HttpClient, 
    private readonly authService: AuthService,
    private readonly appProfile: AppProfile
  ) {}

  getOrganization(orgId: string, token: string): Observable<Organization> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<Organization>(`${this.appProfile.epUs}/organization-ms/organization/${orgId}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  updateOrganization(organizationData: UpdateOrganizationRequest, token: string): Observable<any> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.put(`${this.appProfile.epUs}/organization-ms/organization`, organizationData, { headers }).pipe(
      map(response => {
        return { success: true, data: response };
      }),
      catchError(this.handleError)
    );
  }

  getOrganizationTypes(token: string): Observable<any> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get(`${this.appProfile.epUs}/organization-ms/organization/organization-types`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  activateOrganization(orgId: string, token: string): Observable<any> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.post(`${this.appProfile.epUs}/organization-ms/organization/${orgId}/active`, {}, { headers }).pipe(
      map(response => {
        return { success: true, data: response };
      }),
      catchError(this.handleError)
    );
  }

  deactivateOrganization(orgId: string, token: string): Observable<any> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.post(`${this.appProfile.epUs}/organization-ms/organization/${orgId}/inactive`, {}, { headers }).pipe(
      map(response => {
        return { success: true, data: response };
      }),
      catchError(this.handleError)
    );
  }

  private readonly handleError = (error: any): Observable<never> => {
    const errorMessage = this.getErrorMessage(error);
    console.error('EditOrganizationService Error:', error);
    return throwError(() => ({ message: errorMessage, status: error.status }));
  };

  private getErrorMessage(error: any): string {
    if (error.error instanceof ErrorEvent) {
      return error.error.message;
    }

    return this.getHttpErrorMessage(error);
  }

  private getHttpErrorMessage(error: any): string {
    const statusErrorMap: Record<number, string> = {
      400: error.error?.message || 'Bad request - please check your input',
      401: 'Unauthorized - please log in again',
      403: 'Forbidden - you do not have permission to perform this action',
      404: 'Organization not found',
      409: 'Organization name already exists'
    };

    if (statusErrorMap[error.status]) {
      return statusErrorMap[error.status];
    }

    if (error.status >= 500) {
      return 'Server error - please try again later';
    }

    return error.error?.message || `Error: ${error.status}`;
  }
}